import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import {
  TrendingUp,
  Users,
  Eye,
  Heart,
  BookOpen,
  Download,
  Mail,
  MessageSquare,
  IndianRupee,
  RefreshCw,
  UserCheck,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ icon: Icon, label, value, sublabel, tint = 'saffron', testid }) => {
  const tints = {
    saffron: 'from-saffron-50 to-saffron-100 text-saffron-700 border-saffron-200',
    gold: 'from-gold-50 to-amber-100 text-amber-800 border-amber-200',
    charcoal: 'from-slate-50 to-slate-100 text-slate-700 border-slate-200',
    green: 'from-emerald-50 to-green-100 text-emerald-700 border-emerald-200',
  };
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl border p-5 bg-gradient-to-br ${tints[tint]} shadow-sm`}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-white/70 flex items-center justify-center">
          <Icon size={20} />
        </div>
        <span className="text-xs font-body font-medium uppercase tracking-wide opacity-70">
          {label}
        </span>
      </div>
      <div className="text-3xl font-heading font-semibold leading-none">{value}</div>
      {sublabel && <div className="text-xs mt-2 opacity-70">{sublabel}</div>}
    </div>
  );
};

const formatINR = (n) => `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const formatShortDate = (isoDate) => {
  const d = new Date(isoDate);
  return d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
};

export const AnalyticsDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSummary = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/analytics/summary`, {
        withCredentials: true,
        timeout: 15000,
      });
      setData(res.data);
      setError(null);
    } catch (e) {
      setError('Failed to load analytics.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, []);

  if (loading) {
    return (
      <div className="p-8 text-center text-charcoal-500" data-testid="analytics-loading">
        Loading analytics…
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-8 text-center text-red-600" data-testid="analytics-error">
        {error}
      </div>
    );
  }
  if (!data) return null;

  const chartData = data.donations_last_7_days.map((d) => ({
    date: formatShortDate(d.date),
    donations: d.count,
    amount: d.amount_inr,
  }));

  return (
    <div className="space-y-8" data-testid="analytics-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-heading text-charcoal-800">Foundation Health</h2>
          <p className="text-sm text-charcoal-500 mt-1">
            Snapshot of donations, engagement, and content performance.
          </p>
        </div>
        <button
          onClick={fetchSummary}
          data-testid="analytics-refresh"
          className="inline-flex items-center gap-2 bg-white border border-charcoal-200 text-charcoal-700 rounded-full px-4 py-2 text-xs font-medium hover:bg-charcoal-50"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Top row — headline stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={IndianRupee}
          label="This Month · Donations"
          value={formatINR(data.this_month.amount_inr)}
          sublabel={`${data.this_month.count} contribution${data.this_month.count === 1 ? '' : 's'}`}
          tint="saffron"
          testid="stat-month-donations"
        />
        <StatCard
          icon={UserCheck}
          label="This Month · Registrations"
          value={formatINR((data.registrations_this_month?.amount_inr) || 0)}
          sublabel={`${data.registrations_this_month?.count || 0} participant${(data.registrations_this_month?.count || 0) === 1 ? '' : 's'}${
            (data.registrations_this_month?.fee_inr || 0) + (data.registrations_this_month?.dakshina_inr || 0) > 0
              ? ` · ₹${(data.registrations_this_month.fee_inr || 0).toLocaleString('en-IN')} fee, ₹${(data.registrations_this_month.dakshina_inr || 0).toLocaleString('en-IN')} dakshina`
              : ''
          }`}
          tint="gold"
          testid="stat-month-registrations"
        />
        <StatCard
          icon={Download}
          label="Receipts Downloaded (30d)"
          value={data.receipt_downloads_30d.toLocaleString('en-IN')}
          sublabel="80(G) PDFs served"
          tint="green"
          testid="stat-receipts-30d"
        />
        <StatCard
          icon={Eye}
          label="All-Time · Page Views"
          value={data.all_time.pageviews.toLocaleString('en-IN')}
          sublabel={`${data.all_time.subscribers.toLocaleString('en-IN')} newsletter subs`}
          tint="charcoal"
          testid="stat-pageviews"
        />
      </div>

      {/* Chart + top pages */}
      <div className="grid lg:grid-cols-5 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-3 bg-white rounded-2xl border border-charcoal-100 p-6 shadow-sm"
          data-testid="donations-7d-chart"
        >
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={18} className="text-saffron-600" />
            <h3 className="font-heading text-lg text-charcoal-800">Donations · Last 7 Days</h3>
          </div>
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0E4D2" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#7B8794' }} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#7B8794' }} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} />
                <Tooltip
                  cursor={{ fill: '#FDF4EB' }}
                  contentStyle={{
                    background: '#FFFFFF',
                    border: '1px solid #F0E4D2',
                    borderRadius: 10,
                    fontSize: 12,
                  }}
                  formatter={(value, key) => {
                    if (key === 'amount') return [formatINR(value), 'Amount'];
                    return [value, 'Donations'];
                  }}
                />
                <Bar dataKey="donations" fill="#E67E22" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        <div className="lg:col-span-2 bg-white rounded-2xl border border-charcoal-100 p-6 shadow-sm" data-testid="top-pages-card">
          <div className="flex items-center gap-2 mb-4">
            <Eye size={18} className="text-saffron-600" />
            <h3 className="font-heading text-lg text-charcoal-800">Top Pages · 30 days</h3>
          </div>
          {data.top_pages.length === 0 ? (
            <p className="text-sm text-charcoal-500">No page views tracked yet.</p>
          ) : (
            <ul className="space-y-2">
              {data.top_pages.map((p) => {
                const max = data.top_pages[0].views;
                const pct = Math.max(6, Math.round((p.views / max) * 100));
                return (
                  <li key={p.path} className="text-sm">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-charcoal-700 truncate mr-2 font-mono text-xs">{p.path}</span>
                      <span className="text-charcoal-500 text-xs shrink-0">{p.views}</span>
                    </div>
                    <div className="h-1.5 bg-cream rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-saffron-500 to-gold-500 rounded-full"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Bottom row — top courses + engagement */}
      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 bg-white rounded-2xl border border-charcoal-100 p-6 shadow-sm" data-testid="top-registrations-card">
          <div className="flex items-center gap-2 mb-4">
            <UserCheck size={18} className="text-saffron-600" />
            <h3 className="font-heading text-lg text-charcoal-800">Top Event & Satsang Registrations</h3>
          </div>
          {(data.top_registered_events || []).length === 0 ? (
            <p className="text-sm text-charcoal-500 py-4">
              No registrations yet. When participants sign up, they'll appear here.
            </p>
          ) : (
            <div className="divide-y divide-charcoal-50">
              {data.top_registered_events.map((c, i) => (
                <div key={`${c.kind}-${c.id}`} className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="w-7 h-7 rounded-full bg-saffron-100 text-saffron-700 flex items-center justify-center text-sm font-semibold shrink-0">
                      {i + 1}
                    </span>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-charcoal-800 truncate">{c.title}</div>
                      <div className="text-xs text-charcoal-500">
                        <span className="capitalize">{c.kind}</span> · {c.registrations} registered
                      </div>
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-saffron-700 shrink-0">
                    {c.revenue_inr > 0 ? formatINR(c.revenue_inr) : 'Free'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-2 bg-white rounded-2xl border border-charcoal-100 p-6 shadow-sm" data-testid="top-courses-card">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen size={18} className="text-saffron-600" />
            <h3 className="font-heading text-lg text-charcoal-800">Top Course Enrollments</h3>
          </div>
          {data.top_courses.length === 0 ? (
            <p className="text-sm text-charcoal-500 py-4">
              No paid course enrollments yet.
            </p>
          ) : (
            <div className="divide-y divide-charcoal-50">
              {data.top_courses.map((c, i) => (
                <div key={c.course_id} className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3">
                    <span className="w-7 h-7 rounded-full bg-saffron-100 text-saffron-700 flex items-center justify-center text-sm font-semibold">
                      {i + 1}
                    </span>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-charcoal-800 truncate">{c.title}</div>
                      <div className="text-xs text-charcoal-500">{c.enrollments} enrollments</div>
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-saffron-700 shrink-0">
                    {formatINR(c.revenue_inr)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-2 grid grid-cols-1 gap-4">
          <div className="bg-white rounded-2xl border border-charcoal-100 p-6 shadow-sm" data-testid="engagement-card">
            <div className="flex items-center gap-2 mb-4">
              <Users size={18} className="text-saffron-600" />
              <h3 className="font-heading text-base text-charcoal-800">Engagement</h3>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-center gap-1.5 text-xs text-charcoal-500 mb-1">
                  <Mail size={12} /> Subscribers
                </div>
                <div className="text-xl font-heading font-semibold text-charcoal-800">
                  {data.all_time.subscribers.toLocaleString('en-IN')}
                </div>
              </div>
              <div>
                <div className="flex items-center gap-1.5 text-xs text-charcoal-500 mb-1">
                  <MessageSquare size={12} /> Contacts
                </div>
                <div className="text-xl font-heading font-semibold text-charcoal-800">
                  {data.all_time.contact_submissions.toLocaleString('en-IN')}
                </div>
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-saffron-200 bg-gradient-to-br from-saffron-50 to-cream p-6" data-testid="last-updated-card">
            <div className="text-xs uppercase tracking-wide text-saffron-700 font-medium mb-2">
              Last refreshed
            </div>
            <div className="text-sm text-charcoal-700 font-body">
              {new Date(data.generated_at).toLocaleString('en-IN', {
                dateStyle: 'medium',
                timeStyle: 'short',
              })}
            </div>
            <p className="text-[11px] text-charcoal-500 mt-3 leading-relaxed">
              Analytics are anonymous — only page paths and coarse device buckets are stored. No IPs
              or full user-agents.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
